import { Component } from '@theme/component';

const KLAVIYO_CLIENT_API = 'https://a.klaviyo.com/client/subscriptions/';
// Klaviyo requires a dated revision header on every client API call.
const KLAVIYO_API_REVISION = '2025-04-15';

// GCE app proxy (subpath `gce`, see shopify.app.toml in jm-gce-shopify-app).
// It verifies the Friendly Captcha response and only then subscribes the
// address, so a scripted request without a solved captcha is rejected.
const NEWSLETTER_PROXY_URL = '/apps/gce/newsletter';

/**
 * Newsletter signup form that subscribes an email address to a Klaviyo list
 * (background request, no page reload). Whether a double-opt-in confirmation
 * mail is sent is controlled by the Klaviyo list settings — the JM lists are
 * expected to have double opt-in enabled.
 *
 * With a Friendly Captcha site key in theme settings the form renders the
 * captcha widget and submits through the GCE app proxy, which verifies the
 * captcha server-side. Without a site key it calls Klaviyo's public client API
 * directly, as before.
 *
 * The Klaviyo account (company ID) is configured once per shop in theme
 * settings; the target list is configured per block instance. The Liquid only
 * renders this element when both IDs are present.
 *
 * @typedef {object} NewsletterSignupRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} errorMessage
 * @property {import('./friendly-captcha.js').FriendlyCaptcha} [captcha]
 *
 * @extends {Component<NewsletterSignupRefs>}
 */
class NewsletterSignup extends Component {
  requiredRefs = ['form', 'emailInput', 'submitButton', 'successMessage', 'errorMessage'];

  /**
   * Handles the form submission. Native validation (email format, required
   * consent checkbox) has already passed when this fires. The disabled
   * submit button provides single-flight semantics for the request.
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const { form, emailInput, submitButton, captcha } = this.refs;
    /** @type {HTMLInputElement | null} */
    const consentCheckbox = form.querySelector('input[name="consent"]');
    const email = emailInput.value.trim();

    submitButton.disabled = true;
    this.#setState('idle');

    try {
      if (captcha) {
        // Inside try: if friendly-captcha.js failed to load, the element has no
        // methods, and the error has to end in the error state, not a dead button.
        captcha.hideMessage();
        await this.#subscribeViaProxy(captcha, email, consentCheckbox);
      } else {
        await this.#subscribeDirectly(email, consentCheckbox);
      }

      form.reset();
      this.#setState('success');
    } catch (error) {
      if (error instanceof Error && error.name === 'CaptchaIncompleteError') {
        captcha?.showMessage('incomplete');
      } else if (error instanceof CaptchaRejectedError) {
        captcha?.showMessage('failed');
      } else {
        // Surface the cause (e.g. 400 for a wrong list ID) so a
        // misconfiguration is distinguishable from a network failure.
        console.error('[newsletter-signup]', error);
        this.#setState('error');
      }
    } finally {
      submitButton.disabled = false;
    }
  };

  /**
   * @param {import('./friendly-captcha.js').FriendlyCaptcha} captcha
   * @param {string} email
   * @param {HTMLInputElement | null} consentCheckbox
   */
  async #subscribeViaProxy(captcha, email, consentCheckbox) {
    const captchaResponse = await captcha.solve();

    let response;
    try {
      response = await fetch(NEWSLETTER_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          companyId: this.dataset.companyId,
          listId: this.dataset.listId,
          email,
          consentCheckbox: consentCheckbox ? consentCheckbox.checked : null,
          captchaResponse,
        }),
      });
    } finally {
      // A response can be verified only once, so every attempt needs a new one.
      captcha.reset();
    }

    if (response.status === 403) throw new CaptchaRejectedError();
    if (!response.ok) throw new Error(`Newsletter proxy failed (${response.status})`);
  }

  /**
   * @param {string} email
   * @param {HTMLInputElement | null} consentCheckbox
   */
  async #subscribeDirectly(email, consentCheckbox) {
    /** @type {Record<string, unknown>} */
    const profileAttributes = {
      email,
      // Explicit marketing consent: without it the profile may land in
      // Klaviyo without a subscription state and no mail would ever be sent.
      subscriptions: {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      },
    };
    if (consentCheckbox) {
      // Persist the checkbox as evidence on the profile — the DOM state is
      // gone after form.reset().
      profileAttributes.properties = {
        newsletter_consent_checkbox: consentCheckbox.checked,
      };
    }

    const response = await fetch(
      `${KLAVIYO_CLIENT_API}?company_id=${encodeURIComponent(this.dataset.companyId || '')}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_API_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.dataset.customSource,
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes,
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: this.dataset.listId,
                },
              },
            },
          },
        }),
      }
    );

    if (!response.ok) throw new Error(`Klaviyo subscription failed (${response.status})`);
  }

  /**
   * @param {'idle' | 'success' | 'error'} state
   */
  #setState(state) {
    const { successMessage, errorMessage } = this.refs;
    successMessage.hidden = state !== 'success';
    errorMessage.hidden = state !== 'error';
    if (state === 'success') successMessage.focus();
    if (state === 'error') errorMessage.focus();
  }
}

/** The GCE app proxy rejected the captcha response (invalid, expired or reused). */
class CaptchaRejectedError extends Error {
  constructor() {
    super('Friendly Captcha response was rejected');
    this.name = 'CaptchaRejectedError';
  }
}

if (!customElements.get('newsletter-signup')) {
  customElements.define('newsletter-signup', NewsletterSignup);
}
